import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import * as AuthActions from '../store/auth/auth.actions';
import { selectAuthStatus } from '../store/auth/auth.selectors';
import { decodeJwtClaims, isJwtExpired } from '../core/auth/jwt';
import { readStoredAccessToken, writeStoredAccessToken } from '../core/auth/token-storage';

@Injectable({
    providedIn: 'root'
})
export class AuthInitializerService {
    private store = inject(Store);

    private _isInitialized = false;

    initializeAuth(): Promise<void> {
        if (this._isInitialized) {
            return Promise.resolve();
        }

        this._isInitialized = true;

        const stored = readStoredAccessToken();
        const storedClaims = decodeJwtClaims(stored);
        const storedValid = Boolean(stored && storedClaims && !isJwtExpired(storedClaims));

        if (storedValid) {
            this.store.dispatch(AuthActions.setAccessToken({ accessToken: stored! }));
            this.store.dispatch(AuthActions.checkAuthSuccess({ isAuthenticated: true }));
            this.store.dispatch(AuthActions.loadCurrentUser());
        } else {
            if (stored) writeStoredAccessToken(null);
            this.store.dispatch(AuthActions.checkAuth({ force: false }));
        }

        // Block bootstrap briefly so protected routes don't flash-redirect before refresh completes.
        return firstValueFrom(
            this.store.select(selectAuthStatus).pipe(
                filter((s) => s.isAuthenticated !== null && !s.loading),
                take(1),
                timeout(4000),
                catchError(() => of(null))
            )
        ).then(() => undefined);
    }
}
